import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityClaim } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save claim data.
 */
@Injectable()
export class IdentityClaimService {
    public static INCLUDE_USER_W_PERSON: string = 'user.person';

    private identityApiBaseUrl: string;
    private cachedClaimsMap: { [apiRequest: string]: IdentityClaim[]; } = {};

    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all claims
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityClaim[]> {
        queryParams = queryParams || [];
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh &&
            this.cachedClaimsMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedClaimsMap[apiRequestUrl].map((claim: IdentityClaim) => claim.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const claims: IdentityClaim[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const claim of bodyData) {
                    claims.push((new IdentityClaim()).deserialize(claim, included));
                }

                this.cachedClaimsMap[apiRequestUrl] = claims;
                return claims.map((claim: IdentityClaim) => claim.clone());
            }));
    }

    /**
     * Add or update the claim
     */
    public createClaim(
        resourceId: string,
        resourceType: string,
        role: string,
        status: string = IdentityClaim.STATUS_PENDING,
        userEmail: string = null,
    ): Observable<IdentityClaim> {
        const claim: IdentityClaim = new IdentityClaim();
        claim.resourceId = resourceId;
        claim.resourceType = resourceType;
        claim.role = role;
        claim.status = status;
        claim.userEmail = userEmail;

        return this.save(claim);
    }

    /**
     * Get a single claim's data
     */
    public get(identityClaimId: string, include: string[] = null): Observable<IdentityClaim> {
        const apiRequestUrl: string = this.getApiUrl(identityClaimId, include);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => new IdentityClaim().deserialize(response)));
    }

    /**
     * Add or update the claim
     */
    public save(claim: IdentityClaim, includes: string[] = null): Observable<IdentityClaim> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(claim.id, includes);

        if (claim.id) {
            // Update existing
            response = this.http.patch(apiRequestUrl, claim.serialize());
        } else {
            // Add a new
            response = this.http.post(apiRequestUrl, claim.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityClaim()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(claimId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/claims/`;

        if (claimId) {
            url += claimId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedClaimsMap = {};
    }
}
